import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, X, Check, Sparkles, ArrowRight, AlertTriangle, ChevronRight } from 'lucide-react';

const CompareRow = ({ label, oldValue, newValue, oldBad, newGood = true }) => (
  <div className="grid grid-cols-3 gap-3 items-center py-3 border-b last:border-b-0">
    <div className="text-sm font-medium text-gray-700">{label}</div>
    <div className={`flex items-start gap-2 text-sm ${oldBad ? 'text-red-700' : 'text-gray-600'}`}>
      {oldBad ? <X className="h-4 w-4 mt-0.5 flex-shrink-0" /> : <Check className="h-4 w-4 mt-0.5 text-gray-400 flex-shrink-0" />}
      <span>{oldValue}</span>
    </div>
    <div className={`flex items-start gap-2 text-sm ${newGood ? 'text-green-700 font-semibold' : 'text-gray-700'}`}>
      <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
      <span>{newValue}</span>
    </div>
  </div>
);

const NailFreeAdvantage = () => {
  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid="nail-free-advantage-page">
      {/* Hero */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/40 to-transparent" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-sm font-bold mb-6">
              <ShieldCheck className="h-4 w-4" /> CRILAM PUR TECHNOLOGY
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-['Outfit'] leading-tight">
              Why Nail-Free Matters
            </h1>
            <p className="text-lg text-[#D4AF37] font-medium mb-3">
              PUR-Bonded doors last 10× longer than traditional nailed doors — no rust spots, no edge lifting, no moisture damage.
            </p>
            <p className="text-white/70 max-w-3xl">
              Traditional doors are held together with nails, staples, and generic water-based glue. The nails rust, the glue fails,
              and the edges lift within 2–3 years. CriLam&apos;s CriLam PUR-Bond™ technology eliminates every one of those failure points.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Side-by-side comparison */}
      <section className="py-16">
        <div className="container mx-auto px-6 lg:px-12 max-w-6xl">
          <h2 className="text-3xl font-bold text-[#004534] mb-8 text-center font-['Outfit']">
            Traditional Door vs CriLam Nail-Free
          </h2>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {/* Traditional door illustration */}
            <div className="bg-white rounded-2xl p-6 border-2 border-red-200 relative overflow-hidden">
              <div className="absolute top-3 right-3 px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                <AlertTriangle className="h-3 w-3 inline mr-1" /> After 3 Years
              </div>
              <div className="aspect-[3/4] bg-gradient-to-b from-amber-100/40 to-amber-200/60 rounded-lg mb-4 relative overflow-hidden">
                <svg viewBox="0 0 120 160" className="w-full h-full" preserveAspectRatio="none">
                  <rect x="10" y="10" width="100" height="140" fill="#B08556" />
                  {/* Rust spots */}
                  <circle cx="15" cy="20" r="2" fill="#8B3A0F" />
                  <circle cx="105" cy="25" r="1.5" fill="#8B3A0F" />
                  <circle cx="15" cy="75" r="2.5" fill="#8B3A0F" />
                  <circle cx="105" cy="80" r="1.8" fill="#8B3A0F" />
                  <circle cx="15" cy="140" r="2" fill="#8B3A0F" />
                  <circle cx="105" cy="145" r="1.5" fill="#8B3A0F" />
                  {/* Edge lifting */}
                  <path d="M10 10 L20 15 L20 40 L10 45 Z" fill="#7A5C3A" />
                  <path d="M10 150 L20 145 L20 120 L10 115 Z" fill="#7A5C3A" />
                  {/* Bubble */}
                  <ellipse cx="60" cy="90" rx="8" ry="4" fill="#7A5C3A" opacity="0.7" />
                  {/* Cracks */}
                  <path d="M50 30 L52 40 L48 55" stroke="#5C3620" strokeWidth="0.8" fill="none" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-red-700 mb-3">Traditional Nailed Door</h3>
              <ul className="space-y-1.5 text-sm text-red-700">
                <li className="flex items-start gap-2"><X className="h-4 w-4 mt-0.5" /> Visible rust spots where nails meet moisture</li>
                <li className="flex items-start gap-2"><X className="h-4 w-4 mt-0.5" /> Edge lifting from generic water-based glue failure</li>
                <li className="flex items-start gap-2"><X className="h-4 w-4 mt-0.5" /> Bubbles and delamination near hinges</li>
                <li className="flex items-start gap-2"><X className="h-4 w-4 mt-0.5" /> Termite entry points at nail holes</li>
                <li className="flex items-start gap-2"><X className="h-4 w-4 mt-0.5" /> 2–3 year visible degradation</li>
              </ul>
            </div>

            {/* CriLam door illustration */}
            <div className="bg-white rounded-2xl p-6 border-2 border-green-300 relative overflow-hidden">
              <div className="absolute top-3 right-3 px-2 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                <ShieldCheck className="h-3 w-3 inline mr-1" /> After 10 Years
              </div>
              <div className="aspect-[3/4] bg-gradient-to-b from-amber-50/40 to-amber-100/60 rounded-lg mb-4 relative overflow-hidden">
                <svg viewBox="0 0 120 160" className="w-full h-full" preserveAspectRatio="none">
                  <rect x="10" y="10" width="100" height="140" fill="#B08556" />
                  {/* No rust, no edges — perfect */}
                  <rect x="10" y="10" width="100" height="140" fill="none" stroke="#7A5C3A" strokeWidth="0.5" />
                  {/* Subtle grain */}
                  <path d="M10 40 Q60 42 110 40" stroke="rgba(0,0,0,0.1)" fill="none" strokeWidth="0.5" />
                  <path d="M10 75 Q60 77 110 75" stroke="rgba(0,0,0,0.1)" fill="none" strokeWidth="0.5" />
                  <path d="M10 115 Q60 117 110 115" stroke="rgba(0,0,0,0.1)" fill="none" strokeWidth="0.5" />
                  {/* Sparkle */}
                  <circle cx="90" cy="30" r="4" fill="white" opacity="0.4" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-green-700 mb-3">CriLam PUR-Bonded Door</h3>
              <ul className="space-y-1.5 text-sm text-green-700">
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5" /> Zero nails · zero rust · sealed forever</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5" /> PUR molecular bond — 5× stronger than water-based glue</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5" /> Zero bubbles · zero edge lifting</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5" /> Termite-proof substrate · sealed entry points</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5" /> 10-year written CriLam guarantee</li>
              </ul>
            </div>
          </div>

          {/* Feature comparison table */}
          <div className="bg-white rounded-2xl p-8 border border-gray-200">
            <h3 className="text-xl font-bold text-[#004534] mb-6 font-['Outfit']">Feature-by-Feature Comparison</h3>
            <div className="grid grid-cols-3 gap-3 pb-3 border-b-2 border-[#004534]">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">Feature</div>
              <div className="text-xs font-bold text-red-600 uppercase tracking-wider">Traditional Door</div>
              <div className="text-xs font-bold text-green-700 uppercase tracking-wider">CriLam Nail-Free</div>
            </div>
            <CompareRow label="Construction" oldValue="Nails + generic glue" newValue="PUR molecular bond · nail-free" oldBad />
            <CompareRow label="Edge Sealing" oldValue="Lifts within 2–3 years" newValue="Sealed for 10+ years · guaranteed" oldBad />
            <CompareRow label="Moisture Resistance" oldValue="Swells in bathrooms" newValue="Zero swelling · 100% waterproof (Acrylic)" oldBad />
            <CompareRow label="Termite Protection" oldValue="Nail holes = entry points" newValue="Sealed substrate · termite-proof" oldBad />
            <CompareRow label="Surface Bubbles" oldValue="Common near hinges" newValue="Absolute zero bubbles" oldBad />
            <CompareRow label="Written Guarantee" oldValue="Usually none · verbal only" newValue="10-year written CriLam guarantee" oldBad />
            <CompareRow label="Lifespan" oldValue="3–5 years before visible damage" newValue="10+ years pristine · lifetime substrate" oldBad />
          </div>
        </div>
      </section>

      {/* PUR Science */}
      <section className="py-14 bg-white border-y">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
          <h2 className="text-3xl font-bold text-[#004534] mb-6 text-center font-['Outfit']">The Science Behind PUR-Bonding</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-[#F7F8F5] rounded-xl border-l-4 border-[#D4AF37]">
              <div className="w-10 h-10 rounded-full bg-[#004534] text-white flex items-center justify-center font-bold mb-3">1</div>
              <h4 className="font-bold text-[#004534] mb-2">Molecular Cross-Linking</h4>
              <p className="text-sm text-gray-600">PUR (Polyurethane Reactive) adhesive forms a molecular network stronger than the substrate itself.</p>
            </div>
            <div className="p-6 bg-[#F7F8F5] rounded-xl border-l-4 border-[#D4AF37]">
              <div className="w-10 h-10 rounded-full bg-[#004534] text-white flex items-center justify-center font-bold mb-3">2</div>
              <h4 className="font-bold text-[#004534] mb-2">Moisture-Curing</h4>
              <p className="text-sm text-gray-600">PUR uses ambient moisture to cure — meaning humidity strengthens the bond over time, not weakens it.</p>
            </div>
            <div className="p-6 bg-[#F7F8F5] rounded-xl border-l-4 border-[#D4AF37]">
              <div className="w-10 h-10 rounded-full bg-[#004534] text-white flex items-center justify-center font-bold mb-3">3</div>
              <h4 className="font-bold text-[#004534] mb-2">Zero Mechanical Fasteners</h4>
              <p className="text-sm text-gray-600">No nails, no staples, no rust vectors. The bond IS the construction — invisible and permanent.</p>
            </div>
          </div>
          <div className="mt-8 p-5 bg-[#D4AF37]/10 rounded-xl border border-[#D4AF37]/30 flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-[#7A6621] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-[#7A6621]">Did You Know?</p>
              <p className="text-sm text-gray-700 mt-1">
                Only a handful of factories in India can operate a PUR lamination line — it requires precise temperature, humidity, and pressure control.
                CriLam operates one of the largest PUR-bonding facilities in the region, right here in Kanpur, UP.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gradient-to-br from-[#004534] to-[#0A0D0C] text-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl text-center">
          <h3 className="text-3xl md:text-4xl font-bold mb-4 font-['Outfit']">Every CriLam Door Is Nail-Free. Period.</h3>
          <p className="text-white/70 mb-8">Acrylic Laminate · Mica · Natural Veneer · PU Paint — all bonded with the same CriLam PUR-Bond™ technology.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/door-designer">
              <button className="px-8 py-4 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-white transition-colors flex items-center gap-2">
                Design Your Nail-Free Door <ArrowRight className="h-5 w-5" />
              </button>
            </Link>
            <Link to="/contact">
              <button className="px-8 py-4 border-2 border-white/40 text-white font-medium rounded-lg hover:bg-white/10">
                Talk to a Dealer <ChevronRight className="h-5 w-5 inline" />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NailFreeAdvantage;
